import ApolloForm from '$/components/ApolloForm';
import { useLocation, useParams } from '@reach/router';

import queryString from 'query-string';
import React, { Suspense } from 'react';

import { FBPixelEvent } from '$/FBPixel';
import {
  AttendeeField,
  EWebinarFragment,
  EWebinarSetFragment,
  EWebinarWithSetFragment,
  FullSetFragment,
  RegisterAttendeeInput,
  RegistrationFormSettings,
  RegistrationFormSettingsInput,
  ScriptFragment,
  Session,
  UpdateEwebinarInput,
  useRegisterAttendeeMutation,
  useUpdateEwebinarMutation,
} from '$/GraphQL/schema/schema';
import { GdprContext } from '$/components/GdprCookie/GdprContext';
import SEO from '$/components/SEO';
import { PageBuilderState, PageMode, PageName } from '$/components/ui/Input/PageBuilder.types';
import config from '$/config';
import useCaptcha from '$/hooks/useCaptcha';
import { useEwebinarSession } from '$/hooks/useEwebinarSession';
import { useSSOAuth } from '$/sections/Home/Profile/TeamIntegrations/Info/SSO/SSOLogin';
import { isValidSessionTime } from '$/sections/Home/WebinarEditor/RegistrationPages/RegistrationForm/utils';
import { getLocale } from '$/utils/getLocale';
import { getSocialRegistrationMedia } from '$/utils/seo';
import { useFBPixelContext } from '$/utils/useFBPixel';
import { WidgetEventType, WidgetInfo, WidgetType } from '$/widget/src/interface';
import { useFormContext } from 'react-hook-form';
import RegistrationFormFields from './RegistrationFormFields';

import { Portal } from '$/components/Portal';
import { useRefetchWebinarPropertiesContext } from '$/components/Property/PropertyProvider';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { useLocalI18n } from '$/hooks/useLocalI18n';
import { useServerContext } from '$/hooks/useServerDateNow';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { AssertNonNull } from '$/utils/assert';
import { getRedirectUrl } from '$/utils/attendee';
import { CaptchaOptions } from '$/utils/captcha';
import { getFormFieldDefaultValue } from '$/utils/getFormFieldDefaultValue';
import { getVisitorId, setVisitorId } from '$/utils/visitor';
import { GDPR_COOKIE_CONSENT_OK_EVENT } from '$/widget/src/utils/gdprConsentEvent';
import { createRefreshSessionsTimeout } from '$/widget/src/utils/createRefreshSessionsTimeout';

import Cookies from 'js-cookie';
import { useWebinarContext, WebinarState } from '../../context';
import { RegFormSettingsForForm, RegisterAttendeeFormValues, RegistrationFormProps } from './types';
import { RegistrationForm } from './RegistrationForm';
import { PageEditorContext, usePageEditorState } from '../PageEditorContext';
import { useRegistrationFormValidationSchema } from './validationSchema';

const ClientScripts = React.lazy(() => import('$/components/ClientScripts/ClientScript'));
const PrivacyModal = React.lazy(() => import('./PrivacyModal'));

const UpdateSessionValueTrigger: React.FC<{ session: Session | undefined }> = ({ session }) => {
  const { setValue } = useFormContext();
  React.useEffect(() => {
    if (!session) return;
    setValue('session', session, { shouldDirty: true });
  }, [session, setValue]);
  return null;
};

const RegistrationFormModal: React.FC<RegistrationFormProps> = ({
  set,
  ewebinar,
  isWidget,
  defaultInfo,
  justInTimeDate,
  justInTimeName,
  onClose,
  onSuccess,
  mode,
}) => {
  const { isAcceptedCookies, setIsAcceptedCookies } = React.useContext(GdprContext);
  const refetchWebinarProperties = useRefetchWebinarPropertiesContext();
  const location = useLocation();
  const query = queryString.parse(location.search) as {
    session?: string;
    source?: string;
    widgetType?: WidgetType;
  } & { [key: string]: string }; // For any propertyName values
  const [submitting, setSubmitting] = React.useState(false);

  const params = useParams();
  const widgetType = query.widgetType;
  const sessionTime = params?.sessionTime ?? defaultInfo?.session ?? query.session;

  const [defaultSessionString, setDefaultSessionString] = React.useState<string | undefined>(() =>
    isValidSessionTime(sessionTime) ? sessionTime : undefined
  );
  const [showConditionsPopup, setShowConditionsPopup] = React.useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = React.useState(false);
  const [resetAfterSave, setResetAfterSave] = React.useState(true);
  const [previewSessions, setPreviewSessions] = React.useState<Session[] | undefined>();
  const [widgetInfo, setWidgetInfo] = React.useState<WidgetInfo | undefined>();
  const [isOpen, setIsOpen] = React.useState(!isWidget);
  const [isOpened, setIsOpened] = React.useState(isOpen);
  const [updateEWebinar] = useUpdateEwebinarMutation();
  const [registerAttendee] = useRegisterAttendeeMutation();
  const { serverDateNow } = useServerContext();

  const captchaOptions: CaptchaOptions = React.useMemo(() => {
    return {
      enabled: true,
      size: 'invisible',
      language: ewebinar.viewerRoomSettings.language ?? undefined,
    };
  }, [ewebinar.viewerRoomSettings.language]);
  const { captchaRef, execute: executeCaptcha } = useCaptcha(
    config.CAPTCHA_SITE_KEY,
    captchaOptions
  );

  const { userInfo, loading } = useSSOAuth(set);
  const { pixelID, FBPixel, FBPixelInitialized } = useFBPixelContext();
  const { sessions: ewebinarSession, refresh: refreshEwebinarSession } = useEwebinarSession({
    ewebinar,
    timezone: LocalTimeZone.name,
    skipFromNow: false,
    justInTimeDate,
    justInTimeName,
    serverDateNow,
  });

  const getDefaultSession = React.useCallback((): Session | undefined => {
    if (!defaultSessionString) {
      return undefined;
    }

    if (defaultSessionString === 'ondemand') {
      return ewebinarSession?.sessions.find(({ isOnDemand }) => isOnDemand);
    }

    return ewebinarSession?.sessions.find(({ value }) => value === defaultSessionString);
  }, [defaultSessionString, ewebinarSession?.sessions]);

  const session = React.useMemo(() => getDefaultSession(), [getDefaultSession]);

  const webinarState = useWebinarContext();
  const draftWebinar: WebinarState['draftWebinar'] | undefined = webinarState?.draftWebinar;
  const interactions = draftWebinar?.interactions;

  const i18n = useLocalI18n(ewebinar.viewerRoomSettings.language ?? 'en');
  const interactionProperties = React.useMemo(
    () =>
      interactions?.map((i) => (i.propertyName ?? '').toLowerCase()).filter((p) => p !== '') ?? [],
    [interactions]
  );

  const validationSchema = useRegistrationFormValidationSchema({
    i18n,
    registrationFormSettings: ewebinar.registrationFormSettings,
    refreshEwebinarSession,
    setShowConditionsPopup,
    usedProperties: interactionProperties,
  });

  const onWidgetClose = (): void => {
    if (isWidget) {
      window &&
        window.xprops &&
        window.xprops.emit({
          event: WidgetEventType.FormClose,
        });
    }
  };

  const isOpenedChanged = useHasChangedShallow([isOpened]);
  const isAcceptedCookiesChanged = useHasChangedShallow([isAcceptedCookies]);
  React.useEffect(() => {
    if (!isOpenedChanged && !isAcceptedCookiesChanged) return;
    if (!isOpened || !isWidget) return;
    if (isOpenedChanged) {
      // open form the first time
      // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
      (window as any).__run_user_scripts?.('registrationForm');
    }
    if (isAcceptedCookies) {
      if (Cookies.get('gdpr-cookie-consent') === 'true') return; // cookies already accepted in the iframe, __run_user_scripts will run all the form's scripts, no need to fire the event
      const cookiesAcceptedEvent = new Event(GDPR_COOKIE_CONSENT_OK_EVENT);
      window.dispatchEvent(cookiesAcceptedEvent);
    }
  }, [isAcceptedCookies, isAcceptedCookiesChanged, isOpened, isOpenedChanged, isWidget]);

  React.useEffect(() => {
    if (isOpen && !isOpened) {
      setIsOpened(true);
    }
  }, [isOpen, isOpened]);

  React.useEffect(() => {
    if (isWidget && window && window.xprops && window.xprops.emit) {
      if (ewebinarSession?.sessions.length === 0) {
        window.xprops.emit({
          event: WidgetEventType.HasNoSession,
        });
      }
    }
  }, [ewebinarSession?.sessions, isWidget]);

  React.useEffect(() => {
    const sessions = ewebinarSession?.sessions ?? [];
    const timeout = createRefreshSessionsTimeout(sessions, () => {
      refreshEwebinarSession();
    });
    return (): void => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [ewebinarSession?.sessions, refreshEwebinarSession]);

  React.useEffect(() => {
    document.body.style.height = '100%';
    if (isWidget) {
      document.body.style.background = 'transparent';
    }
  }, [isWidget]);

  React.useEffect(() => {
    if (mode !== PageMode.public || !FBPixelInitialized || !isOpen || !isAcceptedCookies) return;
    // FB Pixel track Visited event
    if (!isWidget) {
      FBPixel.pageView();
    }
    FBPixel.trackCustom(FBPixelEvent.OpenRegForm, undefined);
  }, [FBPixel, FBPixelInitialized, isOpen, isWidget, mode, isAcceptedCookies]);

  const state: PageBuilderState = usePageEditorState({
    mode,
    set,
    ewebinar,
    pagename: PageName.registrationForm,
  });

  React.useEffect(() => {
    if (isWidget) {
      try {
        if (!window.eWebinarWidget) {
          const { createWidget } = require('$/widget/src/widget');
          window.eWebinarWidget = createWidget();
        }

        window.xprops?.on((e) => {
          switch (e.event) {
            case WidgetEventType.AcceptedCookies:
              setIsAcceptedCookies(true);
              return;
            case WidgetEventType.TrackPageView:
              FBPixel.trackCustom(FBPixelEvent.VisitRegistration, undefined);
              break;
            case WidgetEventType.FormOpen: {
              const { session, widgetInfo, previewData } = e.data;
              if (previewData) {
                // open from the WidgetConfig
                setPreviewSessions(previewData.sessions);
              } else {
                setIsOpen(true);
              }
              if (widgetInfo) {
                setWidgetInfo(widgetInfo);
              }
              session && setDefaultSessionString(session);
            }
          }
        });

        // wait the form ready
        window.xprops?.emit({
          event: WidgetEventType.IFrameReady,
          data: {
            ewebinar,
          },
        });
      } catch {
        // init widget failed
      }
    }
  }, [FBPixel, ewebinar, isWidget, set.id, setIsAcceptedCookies]);

  const initialAttendeeFields = React.useMemo(() => {
    let fields: Record<string, string> = {};

    // Grab any passed in fields via propertyName
    ewebinar.registrationFormSettings.fields.map((field, index) => {
      if (!field.propertyName) {
        return;
      }

      if (defaultInfo && defaultInfo?.attendeeFields) {
        const attendeeField = defaultInfo.attendeeFields.find(
          (attendeeField) =>
            field.propertyName?.toLowerCase() === attendeeField.propertyName?.toLowerCase()
        );
        if (attendeeField) {
          fields[`attendeeFields.${index}.value`] = attendeeField?.value;
        }
        return;
      }

      const defaultValue =
        field.propertyName && getFormFieldDefaultValue(field.propertyName, query);
      if (defaultValue) {
        fields[`attendeeFields.${index}.value`] = defaultValue;
      }
    });

    const visitor = widgetInfo?.visitor;
    const visitorName = visitor
      ? [visitor.firstName ?? '', visitor.lastName ?? ''].filter(Boolean).join(' ')
      : undefined;

    const defaultInfoFromParamsAndCookie = {
      email: getFormFieldDefaultValue('email', query) ?? visitor?.email ?? '',
      name: getFormFieldDefaultValue('name', query) ?? visitorName ?? '',
      firstName: getFormFieldDefaultValue('firstName', query) ?? visitor?.firstName ?? '',
      lastName: getFormFieldDefaultValue('lastName', query) ?? visitor?.lastName ?? '',
    };
    // Override values for attendee fields if attendee logged in via SSO
    const firstName =
      userInfo?.given_name ?? defaultInfo?.firstName ?? defaultInfoFromParamsAndCookie.firstName;
    const lastName =
      userInfo?.family_name ?? defaultInfo?.lastName ?? defaultInfoFromParamsAndCookie.lastName;
    const name =
      firstName || lastName
        ? `${firstName} ${lastName}`.trim()
        : defaultInfoFromParamsAndCookie.name;
    fields = {
      ...fields,
      firstName,
      lastName,
      name,
      email: userInfo?.email ?? defaultInfo?.email ?? defaultInfoFromParamsAndCookie.email,
    };

    return fields;
  }, [
    ewebinar.registrationFormSettings.fields,
    widgetInfo?.visitor,
    query,
    userInfo?.given_name,
    userInfo?.family_name,
    userInfo?.email,
    defaultInfo,
  ]);

  const regFormScripts = React.useMemo(() => {
    if (isWidget) return [];
    return (
      set?.useScripts?.reduce((formScripts, s) => {
        if (!s.registrationForm) {
          return formScripts;
        }
        return [...formScripts, s.script];
      }, [] as ScriptFragment[]) || []
    );
  }, [isWidget, set?.useScripts]);

  // const ewebinarSession = sessionsQuery.data?.ewebinarSessions ?? undefined;
  // Check if page is public and ewebinarSession has no session return null
  if (
    mode === PageMode.public &&
    !isWidget &&
    (!ewebinarSession || (ewebinarSession && ewebinarSession?.sessions.length === 0))
  ) {
    return null;
  }
  if (loading) return null;

  const regFormComp = new RegistrationForm();
  const ewebinarWithSet: EWebinarWithSetFragment = {
    ...(ewebinar as EWebinarFragment),
    set: set as EWebinarSetFragment,
  };

  const getNames = (values: RegisterAttendeeFormValues): [string, string | undefined | null] => {
    if (values.useFullNameField) {
      const fullName = values.name.toString() ?? '';
      const firstName = fullName.split(' ')[0];
      const lastName = fullName.split(' ').slice(1).join(' ');
      return [firstName, lastName];
    }
    return [values.firstName!, values.lastName];
  };

  const onSubmit = async (
    formValues: Partial<RegistrationFormSettings> | RegisterAttendeeFormValues
  ): Promise<void> => {
    if (mode === PageMode.public) {
      if (previewSessions) {
        // prevent submit when open in widget preview
        return;
      }

      // Copy object
      const values = { ...formValues } as RegisterAttendeeFormValues;

      const attendeeFields = values.attendeeFields;
      const checkedConsent = values.checkedConsent ? values.checkedConsent.toString() : 'N/A';
      // Ignore First/Last and Email
      const [firstName, lastName] = getNames(values);
      attendeeFields.splice(0, 3);
      const additionalFields = (attendeeFields as AttendeeField[])
        .filter((field) => {
          return (
            field &&
            (!field?.propertyName ||
              !['firstName', 'lastName', 'email', 'name'].includes(field.propertyName))
          );
        })
        .map((field: AttendeeField) => {
          const { fieldName, propertyName, value, type, subType } = field;
          return {
            value,
            fieldName: fieldName.trim(),
            propertyName,
            type,
            subType,
          };
        });

      const isOnDemand = Boolean(values.session?.isOnDemand);
      let startTime = values.session.value;
      if (isOnDemand) {
        // This is an On demand session - startTime will be calculated on the backend
        startTime = 'ondemand';
      }

      const attendeeInput: RegisterAttendeeInput = {
        setId: set.id,
        attendeeFields: additionalFields,
        firstName,
        lastName,
        email: AssertNonNull(values.email),
        startTime,
        sessionType: values.session.type,
        timezone: LocalTimeZone.name,
        checkedConsent,
        locale: getLocale(),
        referrer: widgetInfo?.referrer ?? document.referrer,
        source: widgetInfo?.source ?? query.source,
        origin: widgetInfo?.parentUrl ?? document.location.href,
        visitorId: widgetInfo?.visitorId ?? getVisitorId(),
        widgetType,
      };

      const captchaPromise = executeCaptcha();
      const captchaResponse = await captchaPromise;

      await registerAttendee({
        variables: { data: { ...attendeeInput, captchaResponse } },
        update: async (_cache, mutationResult) => {
          // set setResetAfterSave = false to prevent the form from resetting while
          // the user is still looking at given we're going to redirect
          setResetAfterSave(false);

          if (mutationResult.data && mutationResult.data.registerAttendee) {
            setSubmitting(true);
            const attendee = mutationResult.data.registerAttendee;
            await FBPixel.trackCompleteRegistration(pixelID, attendee.email);
            const urlRedirect = await getRedirectUrl(attendee, ewebinar, i18n);
            setVisitorId(attendee.visitorId);

            if (onSuccess) {
              setTimeout(() => {
                window.open(urlRedirect, '_blank');
              }, 0);
              onSuccess(attendee.id, attendee.set.id, attendee?.sessionType);
              return;
            }

            if (isWidget) {
              window.xprops.emit({
                event: WidgetEventType.Register,
                data: {
                  id: mutationResult.data.registerAttendee.id,
                  joinLink: urlRedirect,
                },
              });
            } else {
              setTimeout(() => {
                // Do this within timeout to wait for Submit to finish and the form was reset
                // then the isDirty state of the form will be updated to false
                // which turns off Unsaved changes flags and therefore prevents the
                // "Do you want to leave this page" dialog

                // However, we do want to keep the spinner going
                document.location.href = urlRedirect;
              }, 300);

              return;
            }
          }
        },
        context: {
          headers: attendeeInput.visitorId
            ? {
                'xsrf-token': attendeeInput.visitorId,
              }
            : {},
        },
      });

      return;
    } else {
      const data = regFormComp.prepareSubmit(
        formValues as RegFormSettingsForForm,
        { ...set, draftWebinar: ewebinar } as FullSetFragment
      ) as UpdateEwebinarInput;

      const formSettingsInput = data.registrationFormSettings as RegistrationFormSettingsInput;

      const questionChanged =
        formSettingsInput.fields.length > ewebinar.registrationFormSettings.fields.length ||
        formSettingsInput.fields.some(
          ({ fieldName }, i: number) =>
            fieldName !== ewebinar.registrationFormSettings.fields[i].fieldName
        );
      const collectedFormFieldsConsent =
        ewebinar.registrationFormSettings.collectedFormFieldsConsent ||
        formSettingsInput.collectedFormFieldsConsent;
      if (!collectedFormFieldsConsent && questionChanged) {
        setShowPrivacyPopup(true);
        // throw error for Apollo form knows submit fail
        throw new Error('Need to consent info collection permission.');
      }

      // editing form
      try {
        await updateEWebinar({
          variables: {
            data,
          },
        });
        await refetchWebinarProperties();
      } catch (e) {
        console.error('Submit error ', e);
        throw e;
      }
    }
  };

  if (!i18n) return null;

  const modal = (
    <div lang={ewebinar.viewerRoomSettings.language ?? 'en'}>
      <div id='optionFieldsModal' />
      <ApolloForm
        shouldUnregister={false}
        showUnsavedChangesDialog={mode !== PageMode.public}
        showErrorOnToast
        resetAfterSave={resetAfterSave}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        defaultValues={{
          ...regFormComp.prepareDefaultValues(ewebinarWithSet),
          session,
          ...initialAttendeeFields,
        }}
      >
        <PageEditorContext.Provider value={state}>
          <SEO
            {...getSocialRegistrationMedia(set)}
            url={`${set.urls.registration}/register/form`}
            titleTemplate={`%s | Webinar registration form`}
            canonicalUrl={set.urls.registration}
          />

          {[PageMode.public].includes(mode) && isOpened && !isWidget && (
            <ClientScripts set={set} scripts={regFormScripts} />
          )}

          {showPrivacyPopup && !ewebinar.registrationFormSettings.collectedFormFieldsConsent && (
            <Suspense>
              <PrivacyModal onClose={(): void => setShowPrivacyPopup(false)} />
            </Suspense>
          )}
          <RegistrationFormFields
            i18n={i18n}
            state={state}
            isWidget={isWidget}
            widgetType={widgetType}
            onClose={isWidget ? onWidgetClose : onClose}
            registrationFormSettings={ewebinar.registrationFormSettings}
            setShowConditionsPopup={setShowConditionsPopup}
            showConditionsPopup={showConditionsPopup}
            sessions={
              // override the sessions when has no session for the widget review
              previewSessions && ewebinarSession?.sessions.length === 0
                ? { ...ewebinarSession!, sessions: previewSessions }
                : ewebinarSession
            }
            widgetInfo={widgetInfo}
            submitting={submitting}
            captchaRef={captchaRef}
          />
          <UpdateSessionValueTrigger session={session} />
        </PageEditorContext.Provider>
      </ApolloForm>
    </div>
  );

  if (mode !== PageMode.public) {
    // Render regForm in a portal to fix issue of nested Form
    return <Portal containerId={'regFormPortalContainer'}>{modal}</Portal>;
  }

  return modal;
};

export default RegistrationFormModal;
